/**
 * Product Management System - Moon Moon Agency v2.1
 * Shopify product page with variant selection, media updates, and AJAX
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const PRODUCT_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,
	
	
	// Selectors
	selectors: {
		variantSelector: 'variant-selector',
		productForm: '#product-form',
		variantInput: '[data-selected-variant-id]',
		productReload: '[data-product-reload]',
		variantFormValue: '[data-variant-value]',
		variantCheckbox: '[data-variant-input]:checked'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */

// Product Event Emitter

/* Wrapper le plus proche (`closest`) et non un `:not()` d'ancetre : le bloc
   prix de la section est lui-meme un `.mm-product-form-component-price` qui
   englobe celui du snippet, sans l'attribut. */
const productPriceNodes = (selector) => [...document.querySelectorAll(selector)].filter((el) => {
	const wrapper = el.closest('[data-money-sample]');
	return wrapper
		&& wrapper.classList.contains('mm-product-form-component-price')
		&& !wrapper.hasAttribute('data-price-configurateur');
});

// DOM Selectors Cache for global elements
const ProductDOM = {
	get stickyCTA() { return document.querySelector('[data-sticky-cta]'); },
	get classicCTA() { return document.querySelector('[data-cta]'); },
	/* Seulement le prix de la fiche (bloc et barre sticky), hors configurateur :
	   `[data-price]` seul attrapait aussi les boutons du configurateur et les
	   cartes, et ecrasait le total du configurateur par prix d'accueil x quantite. */
	get priceElements() { return productPriceNodes('[data-price]'); },
	get comparePriceElements() { return productPriceNodes('[data-compare-price]'); }
};

// Product Data Management
const ProductManager = {
	updateVariantId(container, variantId) {
		if (!variantId) return;
		
		const variantInput = container?.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		if (variantInput && variantInput.value !== variantId.toString()) {
			const oldValue = variantInput.value;
			variantInput.value = variantId;
			window.log('info', 'Product', 'Variant ID updated', { 
				oldVariantId: oldValue, 
				newVariantId: variantId,
				inputElement: variantInput.id || 'no-id',
				containerElement: container?.id || 'no-id'
			});
			window.emit('product:variant-input-updated', { variantId, form: container });
		}
	},

	updateReloadElements(html) {
		const elementsToUpdate = document.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		const newElements = html.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		
		window.emit('product:product-reload-start', { elementsCount: elementsToUpdate.length });

		let updatedCount = 0;
		
		elementsToUpdate.forEach((currentElement, index) => {
			// Try to match by ID first, then by specific class, fallback to index
			let newElement = null;
			
			if (currentElement.id) {
				newElement = html.getElementById(currentElement.id);
			} else if (currentElement.className) {
				// Match by specific component classes for better targeting
				const classList = Array.from(currentElement.classList);
				const specificClass = classList.find(cls => 
					cls.includes('component-price') || 
					cls.includes('component-quantity') || 
					cls.includes('component-cta') ||
					cls.includes('sticky-cta')
				);
				
				if (specificClass) {
					const candidates = html.querySelectorAll(`.${specificClass}[data-product-reload]`);
					newElement = candidates[0];
				}
			}
			
			// Fallback to index-based matching
			if (!newElement) {
				newElement = newElements[index];
			}
			
			if (newElement && this.shouldUpdateElement(currentElement, newElement)) {
				try {
					currentElement.outerHTML = newElement.outerHTML;
					updatedCount++;
					window.emit('product:product-reload-element-updated', { 
						elementIndex: index,
						matchType: currentElement.id ? 'id' : (currentElement.className.includes('component') ? 'class' : 'index')
					});
				} catch (error) {
					window.log('warn', 'Product', 'Element update failed', { 
						index, 
						elementId: currentElement.id || 'no-id',
						elementTag: currentElement.tagName,
						elementClasses: currentElement.className,
						error: error.message 
					});
				}
			}
		});

		window.log('info', 'Product', 'DOM reload completed', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount,
			skippedElements: elementsToUpdate.length - updatedCount,
			elementTypes: [...new Set(Array.from(elementsToUpdate).map(el => el.tagName.toLowerCase()))]
		});
		
		window.emit('product:product-reload-complete', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount
		});
	},

	/* outerHTML et non innerHTML : un changement porte par l'element lui-meme
	   (ex. classe `mm-product-buy__qtty--hidden` sur variante epuisee) doit aussi
	   declencher le remplacement.
	   `data-reload-key` : pour un contenu modifie par le JS une fois monte
	   (carrousel Splide), l'outerHTML differe toujours ; on compare alors la cle
	   rendue par le Liquid, sans quoi le carrousel repartirait de zero a chaque
	   changement de variante. */
	shouldUpdateElement(currentElement, newElement) {
		if (currentElement.dataset.reloadKey !== undefined) {
			return currentElement.dataset.reloadKey !== newElement.dataset.reloadKey;
		}

		const currentContent = currentElement.outerHTML?.trim();
		const newContent = newElement.outerHTML?.trim();
		return currentContent !== newContent;
	}
};


/*********************************************************************************************
 * 🔄 VARIANT SELECTION LOGIC
 */
class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.cached = new Map();
		this.currentVariant = null;
		this.options = [];
		this.variantData = null;
		// Jeton de sequence : sur changements de variante rapides, ignore les reponses arrivant
		// dans le desordre (une reponse lente ne doit pas ecraser le DOM d'une variante plus recente).
		this._reqSeq = 0;
		
		this.container = this.closest(PRODUCT_CONFIG.selectors.productForm);
		if (!this.container) {
			window.log('error', 'Product', 'VariantSelector: No product form container found');
			return;
		}
		
		this.onVariantChange = this.onVariantChange.bind(this);
		this.init();
	}

	init() {
		window.emit('product:variant-selector-init-start');
		
		try {
			this.addEventListener("change", this.onVariantChange);
			this.getSelectedOptions();

			this.getSelectedVariant();
			
			window.emit('product:variant-selector-init-success');
		} catch (error) {
			window.log('error', 'Product', 'Failed to initialize VariantSelector', { error: error.message });
		}
	}

	onVariantChange() {
		window.emit('product:variant-change-start');

		try {
			setTimeout(() => {
				this.getSelectedOptions();
				this.getSelectedVariant();

				if (this.currentVariant) {
					window.emit('product:variant-found', {
						variantId: this.currentVariant.id,
						available: this.currentVariant.available
					});

					// Orchestrate all updates
					this.updateURL();
					this.updateProductData();
					this.updateMediaDisplay();
					this.notifyBackInStock();
				} else {
					window.log('warn', 'Product', 'No matching variant found for options', { options: this.options });
					window.emit('product:variant-not-found', { options: this.options });
				}
			}, 10);
		} catch (error) {
			window.log('error', 'Product', 'Error in variant change', { error: error.message });
		}
	}

	/**
	 * Update product data
	 */
	async updateProductData() {
		// Update variant ID immediately
		ProductManager.updateVariantId(this.container, this.currentVariant.id);

		// Capture le jeton avant le fetch : si une selection plus recente survient pendant
		// l'await, reqId != this._reqSeq et on ignore cette reponse obsolete.
		const reqId = ++this._reqSeq;

		// Fetch and update product data
		const html = await this.fetchVariantData();
		if (reqId !== this._reqSeq) return;

		if (html) {
			ProductManager.updateReloadElements(html);
			this.updateVariantOptions(html);
		}
	}

	/**
	 * Re-sync the Klaviyo back in stock widget with the selected variant.
	 *
	 * Le widget ecoute les `change` sur `body` et lit alors la variante dans
	 * `?variant=` puis `[name="id"]`. Le `change` de l'option lui parvient AVANT
	 * le `setTimeout` de `onVariantChange` : il memorise la variante precedente et
	 * la presente dans la pop-up « Me prevenir ». On lui renvoie donc un `change`
	 * une fois l'URL et l'input a jour. L'input est hors de `<variant-selector>` :
	 * pas de boucle avec `onVariantChange`.
	 */
	notifyBackInStock() {
		const variantInput = this.container.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		variantInput?.dispatchEvent(new Event('change', { bubbles: true }));
	}

	/**
	 * Update media display
	 */
	updateMediaDisplay() {
		// Use MediaManager from the separate media module
		if (window.MediaManager?.updateImagesVariant) {
			window.MediaManager.updateImagesVariant(this.currentVariant);
		} else {
			window.log('warn', 'Product', 'MediaManager or updateImagesVariant method not available', {
				mediaManagerExists: !!window.MediaManager,
				hasUpdateMethod: !!window.MediaManager?.updateImagesVariant
			});
		}
	}

	/**
	 * Fetch variant data
	 */
	async fetchVariantData() {
		if (!this.currentVariant?.id) return null;
		
		const baseUrl = this.dataset.url || window.location.pathname;
		const sectionId = this.dataset.section;
		
		if (!sectionId) {
			window.log('warn', 'Product', 'VariantSelector: No section ID provided');
			return null;
		}

		const variantUrl = `${baseUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}`;

		// Check cache first
		if (this.cached.has(variantUrl)) {
			return this.cached.get(variantUrl);
		}

		try {
			// Timeout dur (apiTimeout) : evite une requete variante suspendue indefiniment
			// sur reseau instable (l'AbortError tombe dans le catch existant -> retourne null).
			const response = await fetch(variantUrl, { signal: AbortSignal.timeout(PRODUCT_CONFIG.apiTimeout) });
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			
			const responseText = await response.text();
			const html = new DOMParser().parseFromString(responseText, 'text/html');
			
			// Cache the result
			this.cached.set(variantUrl, html);
			
			window.emit('product:variant-fetch-success', { variantId: this.currentVariant.id });
			return html;
		} catch (error) {
			window.log('error', 'Product', 'Failed to fetch variant data', { error: error.message });
			return null;
		}
	}

	/**
	 * Get selected options from variant inputs
	 */
	getSelectedOptions() {
		const checkboxes = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantCheckbox);
		this.options = Array.from(checkboxes, checkbox => checkbox.value).filter(value => value);
	}

	/**
	 * Get the selected variant based on options
	 */
	getSelectedVariant() {
		const variants = this.getVariantJSON();
		
		if (!variants.length) {
			this.currentVariant = null;
			return;
		}

		this.currentVariant = variants.find(variant => {
			if (!variant.options || !Array.isArray(variant.options)) return false;
			return variant.options.every((option, index) => this.options[index] === option);
		});
	}

	/**
	 * Get variant data from embedded JSON
	 */
	getVariantJSON() {
		if (!this.variantData) {
			const jsonElement = this.querySelector('[type="application/json"]');
			
			if (!jsonElement) {
				window.log('error', 'Product', 'VariantSelector: No JSON data element found');
				return this.variantData = [];
			}
			
			try {
				const jsonText = jsonElement.textContent.trim();
				this.variantData = jsonText ? JSON.parse(jsonText) : [];
			} catch (error) {
				window.log('error', 'Product', 'Failed to parse variant JSON', { error: error.message });
				this.variantData = [];
			}
		}
		return this.variantData;
	}

	/**
	 * Update URL with selected variant
	 */
	updateURL() {
		if (!this.currentVariant?.id) return;
		
		try {
			const baseUrl = this.dataset.url || window.location.pathname;
			const url = new URL(baseUrl, window.location.origin);
			url.searchParams.set('variant', this.currentVariant.id);
			window.history.replaceState({}, '', url.toString());
		} catch (error) {
			window.log('error', 'Product', 'Failed to update URL', { error: error.message });
		}
	}

	/**
	 * Update variant options without breaking event listeners
	 */
	updateVariantOptions(html) {
		const currentOptions = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		const newOptions = html.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		
		if (!newOptions.length) return;

		currentOptions.forEach((currentOption, index) => {
			const newOption = newOptions[index];
			if (!newOption) return;

			const currentInput = currentOption.querySelector('input');
			const newInput = newOption.querySelector('input');
			
			if (currentInput && newInput) {
				// Update only disabled state, preserve checked state
				currentInput.disabled = newInput.disabled;
				
				const currentlyChecked = currentInput.checked;
				currentOption.className = newOption.className;
				
				if (currentlyChecked !== currentInput.checked) {
					currentInput.checked = currentlyChecked;
				}
			}
		});

		this.updateUnavailableLabels(html);
	}

	/**
	 * Recopie l'etat epuise des valeurs d'option depuis le rendu de la variante.
	 *
	 * Le Liquid grise une valeur selon les options choisies avant elle (un coloris
	 * epuise en 20 cm seulement) : l'etat change donc avec la variante, sur le
	 * label et non sur le wrapper recopie plus haut. Le picker du sticky est hors
	 * de `[data-product-reload]` : il passe ici aussi. Appariement par `for`, les
	 * id des inputs etant stables d'un rendu a l'autre.
	 */
	updateUnavailableLabels(html) {
		const unavailableClass = 'mm-product-variant-label--unavailable';
		const labels = document.querySelectorAll(
			`${PRODUCT_CONFIG.selectors.variantFormValue} label[for], [data-sticky-variants] label[for]`
		);

		labels.forEach((label) => {
			const newLabel = html.querySelector(`label[for="${CSS.escape(label.htmlFor)}"]`);
			if (!newLabel) return;

			label.classList.toggle(unavailableClass, newLabel.classList.contains(unavailableClass));
			if (newLabel.hasAttribute('title')) {
				label.title = newLabel.title;
			} else {
				label.removeAttribute('title');
			}
		});
	}
}

/*********************************************************************************************
 * 🏷️ UTILITY FUNCTIONS
 */

/**
 * Change quantity value and update prices
 */
function mmChangeQtty(delta, inputId) {
	const input = document.getElementById(inputId);
	if (!input) return;

	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;
	const minValue = parseInt(input.getAttribute('min'), 10) || 1;

	// Calculate new value with constraints
	const newValue = Math.max(minValue, Math.min(maxValue, currentValue + parseInt(delta, 10)));

	input.value = newValue;
	/* role="spinbutton" : aria-valuenow doit suivre la valeur, sinon le lecteur
	   d'ecran annonce une quantite perimee. */
	input.setAttribute('aria-valuenow', newValue);
	input.dispatchEvent(new Event('change', { bubbles: true }));

	// Update prices
	mmUpdatePrice(newValue);

	// Update button states
	updateQuantityButtonStates(input);
}

/**
 * Update plus button disabled state based on current quantity
 */
function updateQuantityButtonStates(input) {
	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

	// Find the container
	const container = input.closest('.mm-qtty-container');
	if (!container) return;

	// Find plus button
	const plusBtn = container.querySelector('[data-qtty-increase]');

	// Update plus button only
	if (plusBtn) {
		if (currentValue >= maxValue) {
			plusBtn.setAttribute('disabled', '');
		} else {
			plusBtn.removeAttribute('disabled');
		}
	}
}

/**
 * Update displayed prices based on quantity
 */
function mmUpdatePrice(qtty) {
	const quantity = parseInt(qtty, 10);
	if (!quantity || quantity < 1) return;

	window.emit('product:quantity-update-start', { quantity });

	try {
		let updatedElementsCount = 0;

		/* Le prix affiche vient de `money_without_trailing_zeros` cote Liquid, qui
		   suit le `money_format` de la boutique — ici « 1.092 € » : point comme
		   separateur de milliers, decimales masquees quand le montant tombe juste.
		   Reconstruire ce format avec `Intl.NumberFormat` echouait deux fois : il
		   forcait 2 decimales (« 350,00 € ») et utilisait l'espace insecable en
		   locale fr (« 1 092 € »). On repart donc du texte deja rendu par Liquid :
		   ses separateurs et son symbole sont, par construction, les bons. */
		const readGroupSepHint = () => {
			/* `mm-product-price` expose `data-money-sample` : un 1234,56 passe par le
			   meme filtre Liquid, donc porteur du separateur exact du shop. Indispensable
			   quand le prix affiche fait moins de 4 chiffres mais que la multiplication
			   franchit le millier (350 EUR x4) — la locale du navigateur ne donne pas
			   forcement le meme separateur que le money_format Shopify. */
			const sample = document.querySelector('[data-money-sample]')?.dataset.moneySample;
			const fromSample = sample && sample.match(/[\s.,  ](?=\d{3}\b)/);
			if (fromSample) return fromSample[0];

			const all = [...ProductDOM.priceElements, ...ProductDOM.comparePriceElements];
			for (const el of all) {
				const found = el.textContent.match(/[\s.,  ](?=\d{3}\b)/);
				if (found) return found[0];
			}
			return new Intl.NumberFormat(document.documentElement.lang || 'en')
				.format(1000)
				.replace(/\d/g, '');
		};
		const groupSepHint = readGroupSepHint();

		const readTemplate = (element) => {
			const target = element.querySelector('.wd_discount') || element;
			const text = target.textContent.trim();
			/* Le montant est la plus longue suite de chiffres et de separateurs ;
			   ce qui l'entoure (symbole, espaces, code devise) est conserve tel quel. */
			const match = text.match(/[\d][\d\s.,  ]*\d|\d/);
			if (!match) return null;
			const rendered = match[0];
			/* Decimales = groupe final de 1 ou 2 chiffres precede d'un separateur,
			   les groupes de milliers faisant toujours 3 chiffres. */
			const decimalMatch = rendered.match(/([.,])(\d{1,2})$/);
			const decimalSep = decimalMatch ? decimalMatch[1] : ',';
			/* A defaut de separateur visible sur ce montant, on reprend l'indice
			   collecte sur les autres prix de la page. */
			const groupSep = (rendered.match(/[\s.,  ](?=\d{3}\b)/) || [])[0] || groupSepHint;
			return {
				target,
				prefix: text.slice(0, match.index),
				suffix: text.slice(match.index + rendered.length),
				decimalSep,
				groupSep
			};
		};

		const formatMoney = (amount, tpl) => {
			const hasCents = Math.round(amount * 100) % 100 !== 0;
			const fixed = amount.toFixed(hasCents ? 2 : 0);
			let [intPart, decPart] = fixed.split('.');
			if (tpl.groupSep) {
				intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, tpl.groupSep);
			}
			return tpl.prefix + intPart + (decPart ? tpl.decimalSep + decPart : '') + tpl.suffix;
		};

		/* Le prix barre suit la meme multiplication que le prix courant :
		   sinon, en passant a 2 exemplaires, on affichait "60 EUR barre / 84 EUR",
		   ce qui laissait croire a une hausse au lieu d'une remise. */
		const applyQuantity = (element, rawValue) => {
			const value = parseFloat(rawValue);
			if (isNaN(value)) return false;
			/* Le moneyFormat du shop enveloppe le montant dans un
			   <span class=wd_discount> : on ecrit dedans quand il existe,
			   sinon `textContent` sur le parent supprimerait ce span et le
			   prix perdrait sa mise en forme. */
			const tpl = readTemplate(element);
			if (!tpl) return false;
			tpl.target.textContent = formatMoney((value * quantity) / 100, tpl);
			return true;
		};

		ProductDOM.priceElements.forEach((el) => {
			if (applyQuantity(el, el.dataset.price)) updatedElementsCount++;
		});

		ProductDOM.comparePriceElements.forEach((el) => {
			if (applyQuantity(el, el.dataset.comparePrice)) updatedElementsCount++;
		});

		window.emit('product:quantity-update-success', { 
			quantity, 
			updatedElements: updatedElementsCount 
		});
		
		window.log('info', 'Product', 'Price update completed', {
			quantity,
			updatedPriceElements: updatedElementsCount
		});
	} catch (error) {
		window.log('error', 'Product', 'Failed to update prices', { 
			quantity, 
			error: error.message 
		});
		window.emit('product:quantity-update-error', { 
			quantity, 
			error: error.message 
		});
	}
}

/*********************************************************************************************
 * 📌 STICKY CTA & REASSURANCE
 */

/**
 * Keep the sticky quantity input in sync with the canonical one.
 *
 * Le champ du sticky ne porte pas de `name` : c'est celui de `.mm-product-buy`
 * qui est soumis. On recopie donc la valeur dans les deux sens pour que la
 * quantite choisie en mobile parte bien au panier.
 */
function initializeStickyQuantitySync() {
	const stickyInput = document.querySelector('[data-sticky-qtty] [data-qtty-input]');
	const canonicalInput = document.querySelector('.mm-product-quantity [data-qtty-input]');

	if (!stickyInput || !canonicalInput) return;

	const sync = (from, to) => {
		if (to.value === from.value) return;
		to.value = from.value;
		to.setAttribute('aria-valuenow', from.value);
	};

	stickyInput.addEventListener('change', () => sync(stickyInput, canonicalInput));
	canonicalInput.addEventListener('change', () => sync(canonicalInput, stickyInput));
}

/**
 * Relaie le selecteur de variantes du sticky mobile vers le picker canonique.
 *
 * Les radios du sticky sont detachees du formulaire (`form=` vers un id
 * inexistant) et hors du scope de <variant-selector> : elles ne declenchent donc
 * rien par elles-memes. On repercute le choix sur l'input correspondant du
 * picker, dont le `change` fait tout le reste (prix, medias, variant id).
 *
 * Delegation sur le document : la barre sticky est reconstruite a chaque
 * changement de variante (`data-product-reload`), ce qui detacherait des
 * listeners poses directement sur les inputs.
 */
/**
 * Fermeture du panneau multi-axes du sticky.
 *
 * En multi-axes le summary est masque tant que le panneau est ouvert (le
 * selecteur de quantite prend sa place, Figma 3085:60409) : il ne peut donc plus
 * servir a refermer, ni a la souris ni au clavier. On rend la barre au reste de
 * la page comme le fait la maquette (fond assombri) : clic en dehors ou Escape.
 *
 * La fermeture passe par un clic sur le summary — masque mais toujours dans le
 * DOM — pour rester sur le chemin anime de DetailsAccordionManager.
 */
function initializeStickyMultiPanelDismiss() {
	const closeOpenPanel = () => {
		const details = document.querySelector('.mm-product-sticky-variants--multi[open]');
		if (!details || details._mmAnimating) return;

		const summary = details.querySelector(':scope > summary');
		if (summary) summary.click();
	};

	document.addEventListener('click', (e) => {
		const details = document.querySelector('.mm-product-sticky-variants--multi[open]');
		if (!details) return;

		/* Un clic DANS la barre sticky ne ferme pas : on y choisit ses options,
		   on y ajuste la quantite et on y valide le panier. */
		if (e.target.closest('[data-sticky-cta]')) return;

		closeOpenPanel();
	});

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') closeOpenPanel();
	});
}

/**
 * Rouvre le panneau du sticky apres un changement de fiche produit.
 *
 * Les valeurs du groupe de fiches liees sont de vrais liens : choisir un montage
 * recharge la page, ce qui refermerait le panneau et obligerait a le rouvrir
 * pour regler les axes suivants. Les liens du sticky portent donc `#mm-sticky`,
 * relu ici au chargement.
 *
 * L'ouverture passe par un clic sur le summary (chemin anime de
 * DetailsAccordionManager) et le marqueur est retire de l'URL dans la foulee,
 * pour qu'un rechargement manuel ne rouvre pas le panneau.
 */
function initializeStickyPanelRestore() {
	if (window.location.hash !== '#mm-sticky') return;

	/* Nettoie l'URL avant toute chose : `replaceState` n'ajoute pas d'entree a
	   l'historique, le retour arriere reste donc naturel. */
	history.replaceState(null, '', window.location.pathname + window.location.search);

	const details = document.querySelector('[data-sticky-variants]');
	if (!details || details.open) return;

	const summary = details.querySelector(':scope > summary');
	if (summary) summary.click();
}

function initializeStickyVariantSync() {
	document.addEventListener('change', (e) => {
		const stickyInput = e.target.closest('[data-sticky-variant-input]');
		if (!stickyInput || !stickyInput.checked) return;

		const selector = document.querySelector(PRODUCT_CONFIG.selectors.variantSelector);
		if (!selector) return;

		/* On cible par valeur : les id du sticky sont prefixes, mais la valeur
		   d'option est la meme des deux cotes. */
		const canonical = Array.from(
			selector.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue + ' [data-variant-input]')
		).find((input) => input.value === stickyInput.value);

		/* Le summary est hors de `[data-product-reload]` (il enveloppe le panneau,
		   qui doit survivre au remplacement) : il n'est donc pas rafraichi par le
		   fetch de variante. On recopie la valeur choisie a la main. */
		const details = stickyInput.closest('[data-sticky-variants]');
		const current = details?.querySelector('[data-sticky-current]');
		if (current) current.textContent = stickyInput.value;

		/* Meme raison pour la dimension du mode multi-axes : elle est rendue dans
		   le sticky mais sa source vit dans le picker canonique, lui rafraichi par
		   `data-product-reload`. On la recopie apres le fetch de variante — d'ou le
		   report en fin de tache plutot qu'immediatement. */
		const stickyDims = details?.querySelector('.mm-product-variant-option__dimensions');
		if (stickyDims) {
			const syncDims = () => {
				const source = document.querySelector(
					'.mm-product-variants .mm-product-variant-option__dimensions'
				);
				if (source) stickyDims.innerHTML = source.innerHTML;
			};
			/* `product:product-reload-complete` est emis par `updateReloadElements`
			   une fois le DOM du picker canonique remplace. Ecoute sur `document` :
			   c'est la cible de `window.emit` (cf. mm_utils.js). */
			document.addEventListener('product:product-reload-complete', syncDims, { once: true });
		}

		/* Le choix fait, on replie le panneau : en mobile il occupe une bonne part
		   de l'ecran et masquerait le prix qui vient d'etre mis a jour.
		   On passe par un clic sur le summary plutot que `details.open = false` :
		   DetailsAccordionManager delegue sur le document et n'expose pas de
		   fermeture publique, et un `open = false` direct court-circuiterait son
		   animation (fermeture seche, et `data-mm-open` laisse desynchronise).

		   EXCLU des que le panneau porte autre chose a renseigner : en multi-axes
		   (Figma 3085:60409) comme en plein ecran, ou les variantes cotoient la
		   personnalisation (2818:157332). Le refermer au premier clic empecherait
		   de finir la composition. Le plein ecran a sa propre croix pour sortir. */
		const keepOpen =
			details?.classList.contains('mm-product-sticky-variants--multi') ||
			details?.classList.contains('mm-product-sticky-variants--fullscreen');
		const summary = details?.querySelector(':scope > summary');
		if (!keepOpen && summary && details.open && !details._mmAnimating) summary.click();

		if (!canonical || canonical.checked) return;

		canonical.checked = true;
		canonical.dispatchEvent(new Event('change', { bubbles: true }));
	});
}

/**
 * Initialize sticky CTA behavior
 */
function initializeStickyCTA() {
	const classicCTA = ProductDOM.classicCTA;
	const stickyCTA = ProductDOM.stickyCTA;

	if (!stickyCTA) {
		window.log('warn', 'Product', 'StickyCTA: Required elements not found');
		return;
	}

	// Sous 768px `.mm-product-buy` (donc le CTA classique) est masque par
	// `.mm-desktop` : le sticky est alors le seul point d'achat et reste
	// affiche en permanence. C'est le CSS qui gere cette bascule, le JS ne
	// pilote l'observer que sur les viewports ou le CTA classique existe.
	if (!classicCTA) return;

	try {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				// `mm-sticky-cta--visible` n'a d'effet qu'au-dessus de 768px :
				// en mobile la visibilite est portee par le CSS seul.
				stickyCTA.classList.toggle('mm-sticky-cta--visible', !entry.isIntersecting);
			});
		}, { 
			threshold: 0,
			rootMargin: '0px 0px -10px 0px'
		});

		observer.observe(classicCTA);
		classicCTA._stickyObserver = observer;
		
	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize sticky CTA', { error: error.message });
	}
}

/**
 * Reveal the price recall above the CTA once the main price leaves the viewport.
 *
 * Sur un long parcours (configurateur, personnalisation), le CTA reste a l'ecran
 * mais le prix du bloc `price` est passe sous le header : le client ne voit plus
 * le total qu'il s'apprete a ajouter. Le rappel `.mm-product-buy-price` (rendu
 * dans le bloc `buy_button`) prend alors le relais. Desktop seulement : en
 * mobile, la barre sticky porte deja le prix.
 *
 * La bande du header fixe est retiree de la zone observee : un prix cache sous
 * lui est, pour le client, deja sorti de l'ecran.
 */
function initializeBuyPriceReveal() {
	const buySelector = '.mm-product-buy-price';
	const mainSelector = '.mm-product-form-component-price.mm-desktop:not(.mm-product-buy-price)';
	if (!document.querySelector(buySelector) || !document.querySelector(mainSelector)) return;

	try {
		const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
		let revealed = false;
		const apply = () => document.querySelector(buySelector)?.classList.toggle('mm-product-buy-price--visible', revealed);

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => { revealed = !entry.isIntersecting; });
			apply();
		}, {
			threshold: 0,
			rootMargin: `-${Math.round(header)}px 0px 0px 0px`
		});

		const observeMain = () => {
			observer.disconnect();
			const mainPrice = document.querySelector(mainSelector);
			if (mainPrice) observer.observe(mainPrice);
		};
		observeMain();

		// Les deux prix sont remplaces (outerHTML) au changement de variante : sans
		// rebranchement, l'observer suivrait des noeuds detaches.
		document.addEventListener('product:product-reload-element-updated', () => {
			observeMain();
			apply();
		});
	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize buy price reveal', { error: error.message });
	}
}

/**
 * Retract the sticky CTA once the footer reaches the bottom of the viewport.
 *
 * Barre fixe = elle recouvre le bas du footer en fin de page (newsletter,
 * liens, mentions). On l'efface quand le haut du footer atteint le bas de
 * l'ecran, et pas des son apparition : le footer occupe toute la fin de page,
 * masquer plus tot priverait du CTA sur une longue portion de scroll.
 *
 * On retracte la barre quand le haut du footer remonte au-dessus d'elle. Le
 * `rootMargin` negatif en bas retire la bande occupee par le sticky de la zone
 * d'observation : le footer n'y "entre" donc qu'une fois arrive sous la barre.
 */
function initializeStickyFooterRetract() {
	const stickyCTA = ProductDOM.stickyCTA;
	const footer = document.querySelector('footer');

	if (!stickyCTA || !footer) return;

	try {
		let observer = null;

		const attach = () => {
			if (observer) observer.disconnect();

			const height = Math.round(stickyCTA.getBoundingClientRect().height) || 100;

			observer = new IntersectionObserver((entries) => {
				entries.forEach(entry => {
					stickyCTA.classList.toggle('mm-sticky-cta--at-footer', entry.isIntersecting);
				});
			}, {
				threshold: 0,
				rootMargin: `-${window.innerHeight - height}px 0px 0px 0px`
			});

			observer.observe(footer);
		};

		attach();

		// La hauteur du sticky depend du titre (1 ou 2 lignes) et le seuil depend
		// de celle du viewport : on recalcule quand l'une ou l'autre change.
		if (window.ResizeObserver) {
			new ResizeObserver(attach).observe(stickyCTA);
		}
		window.addEventListener('resize', attach);

		stickyCTA._footerObserver = observer;

	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize sticky footer retract', { error: error.message });
	}
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */

// Define custom element
customElements.define(PRODUCT_CONFIG.selectors.variantSelector, VariantSelector);

/**
 * Deplace la personnalisation dans le panneau du sticky, en mobile seulement.
 *
 * Le bloc `[data-product-customizations]` est rendu UNE SEULE FOIS, dans le
 * formulaire produit (son submit est intercepte par mm_product_customizations.js
 * pour composer les deux lignes panier). En mobile la maquette le place non pas
 * dans le flux de la page mais dans le panneau plein ecran du sticky
 * (Figma 2818:157321 : la fiche fermee n'affiche que description et bandeau).
 *
 * On le DEPLACE donc, sans jamais le dupliquer : deux exemplaires enverraient
 * deux jeux de proprietes concurrents dans /cart/add — le probleme que le
 * selecteur de variantes du sticky doit contourner avec des radios detaches.
 * Deplacer un noeud preserve son etat (option choisie, texte saisi) et ses
 * ecouteurs, qui sont delegues sur le document.
 *
 * Un marqueur reste a l'emplacement d'origine pour pouvoir l'y remettre au
 * passage en desktop (rotation de l'ecran, redimensionnement du navigateur).
 */
function initializeStickyCustomizations() {
	const block = document.querySelector('[data-product-customizations]');
	const slot = document.querySelector('[data-sticky-customizations-slot]');
	if (!block || !slot) return;

	/* Ancre de retour : un commentaire ne coute rien au rendu et survit aux
	   remplacements de `[data-product-reload]`, qui ne touchent pas cette zone. */
	const home = document.createComment('mm-customizations-home');
	block.parentNode.insertBefore(home, block);

	const mq = window.matchMedia('(width <= 768px)');

	const place = () => {
		if (mq.matches) {
			if (block.parentNode !== slot) slot.appendChild(block);
		} else if (block.parentNode !== home.parentNode) {
			home.parentNode.insertBefore(block, home.nextSibling);
		}
	};

	place();
	mq.addEventListener('change', place);
}

/**
 * Deplace le configurateur dans le panneau du sticky, en mobile seulement.
 *
 * Meme mecanique et memes raisons que `initializeStickyCustomizations` : le bloc
 * `[data-configurateur]` est rendu UNE SEULE FOIS dans le formulaire produit, et
 * la maquette mobile le place dans le panneau plein ecran « Ma composition »
 * ouvert par « Choisir mon modele » (Figma 2849:163116), pas dans le flux.
 *
 * Le dupliquer monterait DEUX instances du moteur (`_mount` boucle sur
 * `[data-configurateur]`), donc deux interceptions du submit et deux ajouts au
 * panier. Le deplacement preserve l'etat du parcours et ses ecouteurs, delegues
 * sur la racine.
 *
 * `_ownsProductForm()` reste vrai apres deplacement : le panneau du sticky est
 * lui aussi rendu dans le `{% form 'product' %}`. C'est ce qui garde
 * l'interception du submit, la galerie et le prix total fonctionnels.
 */
function initializeStickyConfigurateur() {
	const block = document.querySelector('[data-configurateur]');
	const slot = document.querySelector('[data-sticky-configurateur-slot]');
	if (!block || !slot) return;

	const home = document.createComment('mm-configurateur-home');
	block.parentNode.insertBefore(home, block);

	const mq = window.matchMedia('(width <= 768px)');

	const place = () => {
		/* Un <dialog> ouvert en showModal() vit dans le top layer : le deplacer
		   l'en sort sans emettre `close`. DrawerManager ne repasserait donc jamais
		   `aria-expanded` a false et ne retirerait pas `stop-scrolling` du body —
		   la page resterait bloquee en non-scrollable. On ferme proprement avant
		   de bouger, ce qui declenche le listener et remet tout d'aplomb. */
		block.querySelectorAll('dialog[open]').forEach((dlg) => dlg.close());

		if (mq.matches) {
			if (block.parentNode !== slot) slot.appendChild(block);
		} else if (block.parentNode !== home.parentNode) {
			home.parentNode.insertBefore(block, home.nextSibling);
		}
	};

	place();
	mq.addEventListener('change', place);
}

/**
 * Ouvre/ferme le panneau plein ecran de personnalisation.
 *
 * DetailsAccordionManager anime le block-size du panneau de 0 a son
 * `scrollHeight` : en plein ecran cette cible serait la hauteur du CONTENU, pas
 * celle de l'ecran, et le panneau se figerait a une taille intermediaire. On
 * court-circuite donc le manager en phase de CAPTURE (il ecoute en bulle, on
 * passe forcement avant lui, quel que soit l'ordre de chargement des scripts) et
 * on se contente de basculer `open` + `data-mm-open` — l'apparition est animee
 * en CSS, en opacite et translation, qui ne touchent pas a la hauteur.
 *
 * La croix du bandeau et la touche Escape ferment le panneau. Un clic hors du
 * panneau ne ferme PAS : il occupe tout l'ecran, il n'y a pas de « dehors »
 * cliquable — contrairement au panneau multi-axes, ancre a la barre.
 */
function initializeStickyFullscreenToggle() {
	const details = document.querySelector('[data-sticky-fullscreen]');
	if (!details) return;

	const bar = details.closest('[data-sticky-cta]');

	/* Le panneau s'arrete au-dessus de la barre, qui reste visible (Figma
	   2818:157332). Sa hauteur varie — quantite affichee ou non, prix barre sur
	   deux lignes — donc on la mesure plutot que de la coder en dur. Mesure a
	   chaque ouverture : le prix et la ligne d'action changent avec la variante
	   choisie. Valeur non arrondie : la barre mesure 100,5px (filet de 0,5px),
	   arrondie a 101px le panneau recouvrait ce filet. */
	const syncBarHeight = () => {
		if (!bar) return;
		const h = bar.getBoundingClientRect().height;
		if (h > 0) document.documentElement.style.setProperty('--mm-sticky-bar-height', `${h}px`);
	};

	const panel = details.querySelector('.mm-product-sticky-variants__panel');
	/* Le panneau couvre tout l'ecran : il se comporte en boite de dialogue, sans
	   en etre une. <details> n'apporte ni piege de focus ni inertie du fond — on
	   pose la semantique a la main. */
	if (panel) {
		panel.setAttribute('role', 'dialog');
		panel.setAttribute('aria-modal', 'true');
		const titre = panel.querySelector('.mm-product-sticky-variants__header-title');
		if (titre) {
			if (!titre.id) titre.id = 'mm-sticky-panel-title';
			panel.setAttribute('aria-labelledby', titre.id);
		}
	}

	const FOCUSABLES = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
	const focusablesDuPanneau = () => (panel
		? [...panel.querySelectorAll(FOCUSABLES)].filter((el) => el.offsetParent !== null)
		: []);

	let declencheur = null;

	const setOpen = (open) => {
		/* Le focus revient d'ou il est parti : sans cela il resterait sur un
		   element devenu invisible, et la tabulation repartirait du debut du
		   document (WCAG 2.4.3). */
		if (open) declencheur = document.activeElement;

		details.open = open;
		details.dataset.mmOpen = String(open);

		/* Mesure APRES la bascule : le summary « OPTIONS » disparait a l'ouverture
		   et la ligne d'action passe pleine largeur, ce qui change la hauteur de la
		   barre. La mesurer avant donnerait la valeur de l'etat precedent, et le
		   panneau chevaucherait la barre d'une rangee. */
		if (open) requestAnimationFrame(syncBarHeight);
		/* Le fond de page ne defile plus derriere le panneau : sans cela, le scroll
		   du panneau se propage a la page une fois arrive en butee. */
		document.body.style.overflow = open ? 'hidden' : '';

		if (open) {
			/* Focus sur la croix : premier element de l'en-tete, et la sortie la
			   plus utile a annoncer. */
			requestAnimationFrame(() => {
				const croix = panel && panel.querySelector('[data-sticky-close]');
				(croix || focusablesDuPanneau()[0])?.focus();
			});
		} else if (declencheur && typeof declencheur.focus === 'function') {
			declencheur.focus();
			declencheur = null;
		}
	};

	/* Etat initial explicite : le CSS pilote l'affichage sur [data-mm-open], qui
	   n'existe pas tant que le manager n'a pas tourne. */
	details.dataset.mmOpen = String(details.open);

	document.addEventListener(
		'click',
		(e) => {
			const summary = e.target.closest('summary');
			if (summary && summary.parentElement === details) {
				/* preventDefault ET stopPropagation : le premier neutralise le toggle
				   natif du <details>, le second empeche DetailsAccordionManager de
				   lancer son animation de hauteur sur ce panneau. */
				e.preventDefault();
				e.stopPropagation();
				setOpen(!details.open);
				return;
			}

			if (e.target.closest('[data-sticky-close]')) {
				e.preventDefault();
				setOpen(false);
			}
		},
		true
	);

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && details.open) {
			setOpen(false);
			return;
		}

		/* PIEGE DE FOCUS. Le panneau masque la page sans la rendre inerte : sans
		   ce bouclage, la tabulation sortirait du panneau et parcourrait une fiche
		   produit invisible. La barre d'achat (prix, quantite, CTA) reste dans le
		   cycle : elle est visible sous le panneau et fait partie du dialogue. */
		if (e.key !== 'Tab' || !details.open) return;

		const cibles = [...focusablesDuPanneau(), ...(bar
			? [...bar.querySelectorAll(FOCUSABLES)].filter((el) => el.offsetParent !== null && !panel?.contains(el))
			: [])];
		if (!cibles.length) return;

		const premier = cibles[0];
		const dernier = cibles[cibles.length - 1];
		const actif = document.activeElement;

		if (e.shiftKey && (actif === premier || !cibles.includes(actif))) {
			e.preventDefault();
			dernier.focus();
		} else if (!e.shiftKey && actif === dernier) {
			e.preventDefault();
			premier.focus();
		}
	});
}

/*********************************************************************************************
 * 🚀 INITIALIZATION & ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	window.emit('product:initializing');

	try {
		initializeStickyCTA();
		initializeBuyPriceReveal();
		initializeStickyFooterRetract();
		initializeStickyQuantitySync();
		initializeStickyVariantSync();
		initializeStickyMultiPanelDismiss();
		initializeStickyPanelRestore();
		initializeStickyCustomizations();
		initializeStickyConfigurateur();
		initializeStickyFullscreenToggle();
		window.emit('product:initialized');
		window.log('info', 'Product', 'Product page initialized successfully');
	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize product page', { error: error.message });
	}

	// Initialize button states on page load
	const inputs = document.querySelectorAll('input[data-qtty-input]');
	inputs.forEach((input) => {
		updateQuantityButtonStates(input);
	});

	// Use event delegation for quantity buttons (works even after DOM updates)
	// Product page buttons use data-qtty-decrease/increase
	// Cart buttons use data-cart-decrease/increase (handled by mm_cart.js)
	document.addEventListener('click', (e) => {
		// Handle decrease buttons
		const decreaseBtn = e.target.closest('[data-qtty-decrease]');
		if (decreaseBtn) {
			const inputId = decreaseBtn.dataset.qttyDecrease;
			window.mmChangeQtty(-1, inputId);
			return;
		}

		// Handle increase buttons
		const increaseBtn = e.target.closest('[data-qtty-increase]');
		if (increaseBtn) {
			const inputId = increaseBtn.dataset.qttyIncrease;
			window.mmChangeQtty(1, inputId);
			return;
		}
	});

	// Adjust quantity when product reload completes
	document.addEventListener('product:product-reload-complete', () => {
		const inputs = document.querySelectorAll('input[data-qtty-input]');
		inputs.forEach((input) => {
			const currentValue = parseInt(input.value, 10) || 1;
			const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

			if (currentValue > maxValue) {
				input.value = maxValue;
				input.setAttribute('aria-valuenow', maxValue);
			} else {
				input.setAttribute('aria-valuenow', currentValue);
			}

			updateQuantityButtonStates(input);
		});

		/* A quantite 1, le prix rendu par le Liquid pour la nouvelle variante est
		   deja le bon : le reecrire en JS n'apporte rien et risque de diverger du
		   format serveur. On ne recalcule que si une multiplication est necessaire. */
		if (inputs.length > 0) {
			const newValue = parseInt(inputs[0].value, 10) || 1;
			if (newValue > 1) mmUpdatePrice(newValue);
		}

		/* « Completez votre piece » suit la variante : un carrousel reecrit
		   arrive non monte. SplideManager ignore ceux deja montes. */
		window.SplideManager?.init();
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	// Product events
	['initializing', 'initialized', 'variant-change-start', 'variant-found', 'variant-not-found',
		'quantity-update-start', 'quantity-update-success', 'quantity-update-error'].forEach(event => {
		document.addEventListener(`product:${event}`, e =>
			console.log(`🛍️ [PRODUCT] ${event}:`, e.detail)
		);
	});

}
/*********************************************************************************************
 * 📖 PRODUCT READ MORE MANAGER
 * Troncature de la description produit (Figma node 1218:11970).
 * Le clamp est fait en CSS ; ce module ne fait que reveler le bouton lorsque le
 * texte deborde reellement, et basculer l'etat deplie.
 */
const ProductReadMoreManager = {
	config: {
		selectors: {
			wrapper: '[data-product-readmore]',
			text: '[data-readmore-text]',
			toggle: '[data-readmore-toggle]',
		},
		classes: {
			expanded: 'is-expanded',
			visible: 'is-visible',
		},
	},

	init() {
		const wrappers = document.querySelectorAll(this.config.selectors.wrapper);
		if (!wrappers.length) return;

		wrappers.forEach((wrapper) => this._setup(wrapper));
		this._bindEvents();

		window.log('info', '📖 [PRODUCT_READMORE]', 'Initialized', { count: wrappers.length });
	},

	/**
	 * Revele le bouton uniquement si le texte est effectivement tronque.
	 * Re-teste au resize : un texte tronque en mobile peut tenir en desktop.
	 */
	_setup(wrapper) {
		const text = wrapper.querySelector(this.config.selectors.text);
		const toggle = wrapper.querySelector(this.config.selectors.toggle);
		if (!text || !toggle) return;

		const refresh = () => {
			if (wrapper.classList.contains(this.config.classes.expanded)) return;
			const isTruncated = text.scrollHeight > text.clientHeight + 1;
			toggle.classList.toggle(this.config.classes.visible, isTruncated);
			toggle.classList.toggle('mm-none', !isTruncated);
		};

		refresh();

		if (typeof ResizeObserver !== 'undefined') {
			const observer = new ResizeObserver(() => refresh());
			observer.observe(text);
			wrapper._mmReadMoreObserver = observer;
		}
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			const toggle = e.target.closest(this.config.selectors.toggle);
			if (!toggle) return;

			const wrapper = toggle.closest(this.config.selectors.wrapper);
			if (!wrapper) return;

			const isExpanded = wrapper.classList.toggle(this.config.classes.expanded);
			toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

			const label = isExpanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
			if (label) toggle.textContent = label;
		});
	},
};

/*********************************************************************************************
 * 🗂️ PRODUCT DRAWER MANAGER
 * Panneaux lateraux "Caracteristiques & Entretien" / "Livraison & Retours"
 * (Figma node 2818:97456). Base sur <dialog> natif : le focus trap, la
 * fermeture par Escape et l'inertie de la page sont assures par le navigateur.
 */
/* ProductDrawerManager (dialogs `[data-product-drawer]`) a ete extrait vers
   mm_utils.js : global, il doit aussi servir les dialogs des cartes produit
   sur les pages collection, que ce fichier ne charge pas. */

/*********************************************************************************************
 * 🔍 PRODUCT LIGHTBOX MANAGER
 *
 * Visionneuse plein ecran de la galerie. Repose sur <dialog> natif : le focus
 * piege, la fermeture par Escape et l'inertie de la page sont fournis par le
 * navigateur — ce module ne gere que l'ouverture, la navigation et l'index.
 */
const ProductLightboxManager = {
	config: {
		selectors: {
			lightbox: '[data-product-lightbox]',
			gallery: '.mm-main-product__medias',
			galleryTrigger: '[data-lightbox-open]',
			close: '[data-lightbox-close]',
			prev: '[data-lightbox-prev]',
			next: '[data-lightbox-next]',
			slide: '[data-lightbox-slide]',
			index: '[data-lightbox-index]',
		},
		classes: {
			noScroll: 'stop-scrolling',
		},
	},

	current: 0,

	init() {
		this.lightbox = document.querySelector(this.config.selectors.lightbox);
		if (!this.lightbox) return;

		this.slides = [...this.lightbox.querySelectorAll(this.config.selectors.slide)];
		if (!this.slides.length) return;

		this._bindEvents();
		window.log('info', '🔍 [PRODUCT_LIGHTBOX]', `Initialized (${this.slides.length} medias)`);
	},

	_bindEvents() {
		/* Delegation sur le document : la galerie est re-rendue au changement de
		   variante, des listeners poses sur les images seraient perdus. */
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(this.config.selectors.galleryTrigger);
			if (trigger) {
				/* L'index vient du markup : plus fiable qu'un indexOf sur le DOM,
				   la galerie etant re-rendue au changement de variante. */
				this.open(parseInt(trigger.dataset.lightboxOpen, 10) || 0, trigger);
				return;
			}

			if (e.target.closest(this.config.selectors.close)) {
				this.lightbox.close();
				return;
			}
			if (e.target.closest(this.config.selectors.prev)) {
				this.go(-1);
				return;
			}
			if (e.target.closest(this.config.selectors.next)) {
				this.go(1);
			}
		});

		/* Fleches clavier : attendu en visionneuse plein ecran. Escape est deja
		   gere nativement par <dialog>. */
		this.lightbox.addEventListener('keydown', (e) => {
			if (e.key === 'ArrowLeft') {
				e.preventDefault();
				this.go(-1);
			} else if (e.key === 'ArrowRight') {
				e.preventDefault();
				this.go(1);
			}
		});

		/* `close` couvre toutes les sorties (Escape, bouton, backdrop). */
		this.lightbox.addEventListener('close', () => {
			document.body.classList.remove(this.config.classes.noScroll);
			this.trigger?.focus();
		});
	},

	open(index = 0, trigger) {
		if (typeof this.lightbox.showModal !== 'function') return;

		/* Memorise le declencheur pour lui rendre le focus a la fermeture. */
		this.trigger = trigger || document.activeElement;

		this.show(index);
		this.lightbox.showModal();
		document.body.classList.add(this.config.classes.noScroll);
		window.emit('product:lightbox-opened', { index });
	},

	show(index) {
		/* Modulo positif : -1 doit ramener a la derniere image. */
		const total = this.slides.length;
		this.current = ((index % total) + total) % total;

		this.slides.forEach((slide, i) => {
			slide.toggleAttribute('hidden', i !== this.current);
		});

		const counter = this.lightbox.querySelector(this.config.selectors.index);
		if (counter) counter.textContent = this.current + 1;
	},

	go(delta) {
		this.show(this.current + delta);
		window.emit('product:lightbox-changed', { index: this.current });
	},
};

/*********************************************************************************************
 * 🧭 PRODUCT HEADER OVERLAY MANAGER
 *
 * Mobile (Figma 2818:97465) : la galerie passe sous la navbar, transparente
 * avec logo et icones blancs tant qu'elle survole la photo, puis reprend son
 * fond beige au-dela. Meme mecanique que les heroes (HeroDotsManager) : la
 * galerie est remontee en CSS (mm-main-product.css), ce module bascule les
 * classes du header. Desktop : header plein, rien a faire.
 */
const ProductHeaderOverlayManager = {
	config: {
		selectors: {
			header: '[data-mm-header]',
			gallery: '[data-product-gallery]',
		},
		classes: {
			transparent: 'mm-header--transparent',
			light: 'mm-header--light',
		},
	},

	overGallery: null,
	frame: null,

	init() {
		this.header = document.querySelector(this.config.selectors.header);
		this.gallery = document.querySelector(this.config.selectors.gallery);
		if (!this.header || !this.gallery) return;

		const onScroll = () => {
			if (this.frame) return;
			this.frame = requestAnimationFrame(() => {
				this.frame = null;
				this._update();
			});
		};
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll, { passive: true });
		this._update();
	},

	/**
	 * @param {boolean} [force] - re-applique les classes meme sans changement
	 *   d'etat (la recherche les retire a l'ouverture).
	 */
	_update(force = false) {
		let over = false;
		if (window.isMobile()) {
			const line = this.header.getBoundingClientRect().bottom;
			const r = this.gallery.getBoundingClientRect();
			over = r.top < line && r.bottom > line;
		}

		if (over === this.overGallery && !force) return;
		this.overGallery = over;
		this.header.classList.toggle(this.config.classes.transparent, over);
		this.header.classList.toggle(this.config.classes.light, over);
	},

	/** Appele par SearchManager a la fermeture, comme HeroDotsManager. */
	syncHeaderState() {
		if (!this.header || !this.gallery) return;
		this._update(true);
	},
};

window.ProductHeaderOverlayManager = ProductHeaderOverlayManager;

/* Auto-init des modules de la page produit ADR */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		ProductReadMoreManager.init();
		ProductLightboxManager.init();
		ProductHeaderOverlayManager.init();
	});
} else {
	ProductReadMoreManager.init();
	ProductLightboxManager.init();
	ProductHeaderOverlayManager.init();
}
